/**
 * OrderSale read/write endpoints for odl_app.
 *
 * Sales orders are browsed via EntityGrid (dataSource/apiClient) over
 * odl-app/order-sales/ — the list read stays on the legacy warehouse API
 * (Layer 2), so no list endpoint lives here. This module injects the odl-app
 * OrderSale detail-read + write surface into entitiesApi and re-exports the
 * hooks the order grid, detail drawer, and full order form drive: detail read,
 * create, patch, delete.
 *
 * Create is a NESTED write of the whole order graph (items + iccids) in one
 * POST; patch runs the BE previous-status cascade and the restricted
 * Shipped/Complete -> Cancelled/Deleted gate. The CRUD reads/writes wrap the
 * body in the odl-app { "data": … } envelope, so transformResponse is the
 * plain `b => b.data`. Delete is a 204 no-body, superuser-only (403 otherwise):
 * it omits transformResponse entirely (nothing to unwrap). Field-validation
 * failures surface at error.data.errors; the cancel/delete gate and the
 * superuser 403 surface at error.data.detail.
 *
 * Tags invalidate the OrderSale LIST (+ the touched id) so any RTKQ read
 * consumer refetches; the EntityGrid list rides the MUI dataSource, which tag
 * invalidation does NOT reach — grid callers refresh via the onGridReady
 * `refresh` seam (OrderGrid gridRefreshRef / OrderDetailDrawer onMutated) after
 * a mutation resolves.
 *
 * Contains:
 * - ordersaleApi — the injected endpoint slice
 * - hook re-exports (see the named export block)
 */

import { entitiesApi } from './entitiesApi';

const ordersaleApi = entitiesApi.injectEndpoints({
  endpoints: build => ({
    /**
     * Retrieve a single order (OrderSaleSerializer: nested items/iccids + *_detail).
     *
     * @param {{ id: string|number }} arg
     */
    retrieveOrderSale: build.query({
      query: ({ id }) => ({ url: `order-sales/${id}/` }),
      transformResponse: b => b.data,
      providesTags: (result, error, { id }) => [{ type: 'OrderSale', id }],
    }),

    /**
     * Create an order. Body is the OrderSaleSerializer input; the serializer
     * does a nested write of the whole order graph (items/iccids). Returns
     * detail (201). Field-validation failures surface at error.data.errors.
     *
     * @param {{ body: object }} arg
     */
    createOrderSale: build.mutation({
      query: ({ body }) => ({ url: 'order-sales/', method: 'post', data: body }),
      transformResponse: b => b.data,
      invalidatesTags: [{ type: 'OrderSale', id: 'LIST' }],
    }),

    /**
     * Partial-update an order (status change runs the BE previous-status
     * cascade; the restricted Shipped/Complete -> Cancelled/Deleted gate is
     * BE-side, 403 -> error.data.detail).
     *
     * @param {{ id: string|number, body: object }} arg
     */
    patchOrderSale: build.mutation({
      query: ({ id, body }) => ({
        url: `order-sales/${id}/`,
        method: 'patch',
        data: body,
      }),
      transformResponse: b => b.data,
      invalidatesTags: (result, error, { id }) => [
        { type: 'OrderSale', id: 'LIST' },
        { type: 'OrderSale', id },
      ],
    }),

    /**
     * Delete an order. 204 no-body, superuser-only (non-superuser -> 403,
     * error.data.detail). No transformResponse — nothing to unwrap.
     *
     * @param {{ id: string|number }} arg
     */
    deleteOrderSale: build.mutation({
      query: ({ id }) => ({ url: `order-sales/${id}/`, method: 'delete' }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'OrderSale', id: 'LIST' },
        { type: 'OrderSale', id },
      ],
    }),
  }),
});

export { ordersaleApi };
export const {
  useRetrieveOrderSaleQuery,
  useLazyRetrieveOrderSaleQuery,
  useCreateOrderSaleMutation,
  usePatchOrderSaleMutation,
  useDeleteOrderSaleMutation,
} = ordersaleApi;
